/**
 * Confirmation before any admin configuration change is saved.
 *
 * Pages call fetch() directly for their /admin saves, so rather than touching a dozen
 * pages this wraps window.fetch once: a mutating call to an /admin route first shows a
 * confirmation dialog summarising what is about to be sent. Cancel rejects the call with
 * an AbortError and nothing reaches the server. The server records every confirmed save in
 * the admin audit log (services/firewall middleware/configChangeAudit.ts).
 *
 * The SKIP list mirrors the server's: routes that are not configuration (tests, previews,
 * searches, downloads) and approval decisions (confirmed in the approvals UI itself).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../components/ui';

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const ADMIN_PATH = /^\/(api\/)?admin\//;
const SKIP = /\/(approvals|auth|login|logout|test|preview|dry-run|search|query|verify|export|download|reload-catalog|enroll\w*)(\/|$)/i;
const SECRET_KEY = /pass(word|phrase)?|secret|token|api[-_]?key|private[-_]?key|credential|authorization|bearer/i;

interface PendingChange {
  method: string;
  path: string;
  rows: Array<[string, string]>;
  resolve: (ok: boolean) => void;
}

let listener: ((c: PendingChange) => void) | null = null;
let installed = false;

export function shouldConfirmChange(method: string, path: string): boolean {
  return MUTATING.has(method.toUpperCase()) && ADMIN_PATH.test(path) && !SKIP.test(path);
}

function summarise(body: unknown): Array<[string, string]> {
  if (typeof body !== 'string' || !body) return [];
  let parsed: unknown;
  try { parsed = JSON.parse(body); } catch { return []; }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [['payload', `${JSON.stringify(parsed).slice(0, 200)}`]];
  return Object.entries(parsed as Record<string, unknown>).slice(0, 12).map(([k, v]) => {
    if (SECRET_KEY.test(k)) return [k, '••••••••'] as [string, string];
    const text = typeof v === 'string' ? v : JSON.stringify(v);
    return [k, text.length > 200 ? `${text.slice(0, 200)}…` : text] as [string, string];
  });
}

/** Install the fetch wrapper once (call before rendering the app). */
export function installChangeConfirmation(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    let path = '';
    try {
      const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      path = new URL(raw, window.location.href).pathname;
    } catch { /* not a parseable URL: let it through */ }
    if (listener && path && shouldConfirmChange(method, path)) {
      const ok = await new Promise<boolean>((resolve) =>
        listener!({ method, path, rows: summarise(init?.body), resolve })
      );
      if (!ok) throw new DOMException('Change cancelled by the administrator', 'AbortError');
    }
    return original(input, init);
  };
}

/** Renders the confirmation dialog. Mount once, near the app root. */
export const ChangeConfirmHost: React.FC = () => {
  const { t } = useTranslation();
  const [queue, setQueue] = useState<PendingChange[]>([]);

  useEffect(() => {
    listener = (c) => setQueue((q) => [...q, c]);
    return () => { listener = null; };
  }, []);

  const current = queue[0];
  if (!current) return null;
  const settle = (ok: boolean) => {
    current.resolve(ok);
    setQueue((q) => q.slice(1));
  };
  const destructive = current.method === 'DELETE';

  return (
    <Modal
      title={t('components.confirmChange.title', { defaultValue: 'Confirm configuration change' })}
      onClose={() => settle(false)}
      maxWidth={560}
      footer={
        <>
          <button className="ent-btn" onClick={() => settle(false)}>
            {t('components.confirmChange.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button className={`ent-btn ${destructive ? 'danger' : 'primary'}`} onClick={() => settle(true)} autoFocus>
            {destructive
              ? t('components.confirmChange.confirmDelete', { defaultValue: 'Confirm delete' })
              : t('components.confirmChange.confirm', { defaultValue: 'Save change' })}
          </button>
        </>
      }
    >
      <p style={{ marginTop: 0, marginBottom: 12 }}>
        {t('components.confirmChange.body', {
          defaultValue: 'You are about to change the platform configuration. This is recorded in the admin audit log.',
        })}
      </p>
      <p style={{ fontFamily: 'var(--mono, monospace)', fontSize: 12, color: 'var(--text-2)' }}>
        {current.method} {current.path}
      </p>
      {current.rows.length > 0 && (
        <table style={{ width: '100%', fontSize: 13 }}>
          <tbody>
            {current.rows.map(([k, v]) => (
              <tr key={k}>
                <td style={{ color: 'var(--text-2)', paddingInlineEnd: 12, verticalAlign: 'top', whiteSpace: 'nowrap' }}>{k}</td>
                <td style={{ wordBreak: 'break-word' }}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
};
